import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import TextField from "@/common/components/TextField"
import { ChevronsRight } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { GeneralDocumentValidationSchema, createSchema } from "../../schemas/create.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { SubmitHandler, useForm } from "react-hook-form"
import { useState } from "react"
import Spinner from "@/common/components/Spinner"
import MaskedTextField from "@/common/components/MaskedTextField"
import { FileUploader } from "@/common/components/FileUploader"
import { checkIsPdf } from "@/common/utils/fileValidator"
import SelectController from "@/common/components/SelectController"
import { options as kindOptions, strategy } from "@/features/generalDocuments/constants/kind.constant"
import { useGeneralDocument } from "@/features/generalDocuments/hooks/useGeneralDocument"
import { dateTransformer } from "@/common/utils/dateTransformer"
import { useUpdateGeneralDocument } from "../../hooks/useUpdateGeneralDocument"
import GoBackButton from "@/common/components/GoBackButton";
import { useGoBack } from "@/common/hooks/useGoBack";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper"
import useUserPermissions from "@/common/hooks/useUserPermissions"

const Update = () => {
  const goBack = useGoBack()
  const [file, setFile] = useState<Blob>();
  const [fileName, setFileName] = useState('');
  const [serverError, setServerError] = useState('');
  const { id } = useParams();

  const { mutate, isPending } = useUpdateGeneralDocument({
    id: id,
    onSuccess: () => goBack({ fallback: `/general-documents/${id}` }),
    onError: () => setServerError('generic'),
  });

  const {
    data: document,
  } = useGeneralDocument({
    id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<GeneralDocumentValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      display_name: document?.data.display_name,
      expiration_date: document?.data?.expiration_date ? dateTransformer(document?.data?.expiration_date) : '',
      kind: { value: document?.data.kind, label: strategy[document?.data.kind as keyof typeof strategy] }
    }
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateGeneralDocumentPermission = !isUserPermissionsPending ? hasUserPermission('Admin::GeneralDocuments::Update') : true

  const onSubmit: SubmitHandler<GeneralDocumentValidationSchema> = (data) => {
    if (file) {
      const originalDate = dateTransformer(document?.data?.expiration_date);
      const newDate = data.expiration_date;
      if (originalDate === newDate) {
        setServerError('expiration_date_required');
        return;
      }
    }
    mutate({ form: data, file });
  };

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateGeneralDocumentPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/general-documents" />
            <p className="text-white-400 font-normal">Documentos</p>
            <ChevronsRight color='#ffffffb3' />
            <h2>Documento</h2>
          </div>
          <div className="flex gap-4">
            <Link to={`/general-documents/${id}`}><Button variant='outline-error'>Cancelar</Button></Link>
            <Button type="submit" data-testid="create_document" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" >{isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Confirmar'}</Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {serverError === 'generic' && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <p className="text-lg font-bold">Editar documento</p>
          <div className="flex gap-2">
            <TextField
              label="Nome do documento"
              data-testid="display_name_input"
              className="w-2/4"
              errorMessage={
                serverError === 'Validation failed: Display name has already been taken'
                  ? 'Documento com mesmo nome já existe'
                  : errors?.display_name?.message
                    ? errors?.display_name?.message
                    : undefined
              }
              {...register('display_name')}
            />
            <SelectController
              control={control}
              name="kind"
              label="Tipo de documento"
              dataTestid="kind_selector"
              className="w-1/4"
              errorMessage={errors.kind?.message || undefined}
              options={kindOptions}
            />
            <MaskedTextField
              label="Validade"
              data-testid="expiration_date_input"
              mask='00/00/0000'
              className="w-1/4"
              placeholder="Não informado"
              errorMessage={serverError === 'expiration_date_required'
                ? 'Ao alterar o documento, você precisa alterar a data de validade.'
                : errors.expiration_date?.message || undefined
              }
              {...register('expiration_date')}
            />
          </div>
          <FileUploader
            label=""
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsPdf(file)) {
                return
              }
              setFileName(file.name)
              setFile(file)
            }}
            name="document"
            data-testid="file_input"
            accept="application/pdf"
            fileName={fileName ? fileName : undefined}
            currentFileUrl={file ? URL.createObjectURL(file) : undefined}
          />
        </Box>
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
